import { Popover } from '@mui/material';
import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import { ISelectProps } from '../../interfaces/components/input.interface';
import SelectInputOptionSkeleton from './SelectInputOptionSkeleton';

function SelectInput({
  data = [],
  className = '',
  placeholder = '',
  setValue,
  value = '',
  error = false,
  icon,
  isObject = false,
  propertyName = '',
  listIcon,
  placeholderIcon,
  notReactIcon = false,
  keepPlaceHolderIcon = false,
  useIcon = true,
  disabled = false,
  optionNameClassName = '',
  dropdownLabel = '',
  disableDropdownPadding = false,
  infiniteLoadingRef,
  meta,
}: ISelectProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const selectedIndex = value
    ? data.findIndex((item) => item.value === value)
    : -1;
  const [width, setWidth] = useState<string>('100px');
  const inputBoxRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const toggleOptions = () => {
    setShowDropdown(!showDropdown);
  };

  const rerenderDropdown = () => {
    setWidth((inputBoxRef.current?.offsetWidth as number) + 'px');
  };

  useEffect(() => {
    setAnchorEl(inputBoxRef.current);
  }, [inputBoxRef]);

  return (
    <main className="w-full">
      <div className="relative">
        {/* input box */}
        <button
          ref={inputBoxRef}
          className={`${className} ${
            useIcon ? 'pl-1' : 'pl-3'
          } border px-4 py-1 ${
            error
              ? '!border-red-500 focus-within:border-gray-300'
              : 'border-[#e2e2e2] focus-within:border-sky-500'
          } ${
            disabled ? 'bg-gray-100' : ''
          } flex w-full items-center justify-start rounded-lg`}
          onClick={
            disabled
              ? undefined
              : () => {
                  rerenderDropdown();
                  toggleOptions();
                }
          }
          disabled={disabled}
        >
          {!keepPlaceHolderIcon &&
            useIcon &&
            selectedIndex >= 0 &&
            (!notReactIcon ? (
              <span className="flex h-[43px] w-[43px] items-center justify-center rounded-md border-[8px] border-transparent p-1 text-lg enabled:border-white enabled:bg-blur_primary">
                {!listIcon
                  ? icon
                  : listIcon.map(({ icon, name }) =>
                      name === value ? (
                        <p
                          key={name}
                          className="flex items-center justify-center text-lg text-primary"
                        >
                          {icon}
                        </p>
                      ) : undefined
                    )}
              </span>
            ) : (
              <span className="flex h-[44px] w-[44px] items-center justify-center rounded-md border-[8px] p-1 text-lg enabled:border-white">
                {!listIcon
                  ? icon
                  : listIcon.map(({ icon, name }) =>
                      name === value ? icon : undefined
                    )}
              </span>
            ))}

          {(keepPlaceHolderIcon || selectedIndex < 0) && useIcon && (
            <span className="flex h-[43px] w-[43px] items-center justify-center rounded-md border-white p-1 text-lg">
              {placeholderIcon}
            </span>
          )}

          <div
            className={`flex-1 rounded-lg border-r-[16px] border-transparent py-3 pl-1 text-left text-[16px] focus:outline-none ${
              value == '' ? 'text-grey_2' : 'text-black'
            }`}
          >
            {selectedIndex >= 0 ? data[selectedIndex].name : placeholder}
          </div>
          <Image
            src={'/icons/arrowDown.svg'}
            alt="dropdown icon"
            width={15}
            height={15}
          />
        </button>

        {/* dropdown */}
        <Popover
          anchorEl={anchorEl}
          open={showDropdown}
          onClose={() => {
            setShowDropdown(false);
          }}
          title=""
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <div
            ref={dropdownRef}
            className={`${
              !disableDropdownPadding && 'p-2'
            } max-h-[225px] w-full overflow-y-auto rounded-lg border border-gray-300 bg-white drop-shadow-xl`}
            style={{
              width: width,
            }}
          >
            {dropdownLabel && (
              <div className="my-3 ml-3 px-2 text-sm text-[#737E8C]">
                {dropdownLabel}
              </div>
            )}
            {data.length > 0 &&
              data.map((item, index) => {
                return (
                  <button
                    key={index}
                    className={`z-50 w-full text-left hover:bg-primary/10 ${
                      useIcon ? 'px-2' : 'px-4'
                    } ${
                      item.disabled ? 'cursor-not-allowed' : ''
                    } flex flex-col`}
                    disabled={item.disabled}
                    onClick={() => {
                      isObject
                        ? setValue(propertyName, data[index].value)
                        : setValue(data[index].value);
                      setShowDropdown(false);
                    }}
                  >
                    <div className="flex items-center justify-start">
                      {useIcon &&
                        (!notReactIcon ? (
                          <span className="flex h-[43px] w-[35px] items-center justify-center rounded-md p-1 text-lg">
                            {!listIcon
                              ? icon
                              : listIcon.map(({ icon, name }) =>
                                  name === item.value ? (
                                    <p
                                      key={name}
                                      className="flex items-center justify-center text-lg text-primary"
                                    >
                                      {icon}
                                    </p>
                                  ) : undefined
                                )}
                          </span>
                        ) : (
                          <span className="flex h-[44px] w-[36px] items-center justify-center rounded-md p-1 text-lg">
                            {!listIcon
                              ? icon
                              : listIcon.map(({ icon, name }) =>
                                  name === item.value ? icon : undefined
                                )}
                          </span>
                        ))}

                      <div
                        className={`${optionNameClassName} rounded-lg py-3 pl-1 text-left text-[16px] focus:outline-none `}
                      >
                        {item.name}
                      </div>
                    </div>

                    {item.description && (
                      <div className="ml-4 pb-4 font-normal">
                        {item.description}
                      </div>
                    )}
                    {meta &&
                    index === meta.pageSize * meta.currentPage - 1 &&
                    meta.hasNextPage ? (
                      <div ref={infiniteLoadingRef}>
                        <SelectInputOptionSkeleton />
                      </div>
                    ) : null}
                  </button>
                );
              })}
          </div>
        </Popover>
      </div>
    </main>
  );
}

export default SelectInput;
